<template>
  <OverlayPanelNotification />

  <button
    type="button"
    size="large"
    class="grouped-item-list"
    data-qa="user-menu__toggle"
    @click="userMenu!.toggle"
  >
    <PrmAvatar :label="initials" />
  </button>

  <PrmMenu
    ref="userMenu"
    popup
    data-qa="user-menu"
    :model="items"
    style="width: 18rem;"
  >
    <template #start>
      <div class="p-4 font-weight-bold">
        <PrmAvatar :label="initials" />
        <span class="pl-2">{{ appStore.user?.displayName || '' }}</span>
      </div>
    </template>
    <template
      #item="{
        item, props,
      }"
    >
      <router-link
        v-if="item.routeName"
        v-slot="{
          href, navigate,
        }"
        :to="item.routeName"
        custom
      >
        <a
          :href="href"
          v-bind="props.action"
          class="navbar-header__menu-item"
          :style="item.style"
          @click="navigate"
        >
          <span :class="item.icon" />
          <span class="ml-2">{{ item.label }}</span>
        </a>
      </router-link>
      <button
        v-else-if="item.command"
        v-bind="props.action"
        :style="item.style"
        :disabled="(item.disabled as (boolean | undefined))"
        data-qa="user-menu__item"
        :data-qa-id="item.key"
        type="button"
        class="w-full navbar-header__menu-item"
      >
        <span
          v-if="item.icon"
          :class="item.icon"
        />
        <component
          :is="item.iconComponent"
          v-if="item.iconComponent"
        />
        <span class="ml-2">{{ item.label }}</span>
      </button>
      <div
        v-else
        class="navbar-header__menu-item"
        :style="item.style"
        @hover.capture.stop
        @click.capture.stop
      >
        <span class="mx-4">{{ item.label }}</span>
      </div>
    </template>
    <template
      #end
    >
      <div
        class="p-4 flex-row flex-row_end"
        style="gap: 0;"
      >
        <router-link
          v-slot="{
            href, navigate,
          }"
          :to="{
            name: ERouteName.RELEASE_NOTES,
          }"
          custom
        >
          <a
            :href="href"
            class="navbar-header__menu-item"
            data-qa="user-menu__version"
            @click="navigate"
          >
            <span class="font-bold">{{ packageInfo.version }}</span>
          </a>
        </router-link>
      </div>
    </template>
  </PrmMenu>

  <ModalBugReport
    v-if="showModalBugReport"
    @close="showModalBugReport = false"
  />
</template>

<script setup lang="ts">
import PrmAvatar from '@NitrexGroup/primevue/avatar';
import PrmMenu from '@NitrexGroup/primevue/menu';
import {
  i18n,
} from '@i18n';
import * as Sentry from '@sentry/vue';
import {
  computed,
  shallowRef,
} from 'vue';
import {
  useRoute, useRouter,
} from 'vue-router';

import packageInfo from '#/services/frontend/package.json';
import HomeIcon from '@/assets/icon/home.svg?component';
import {
  keycloakAuthenticationHandler,
} from '@/authentication.ts';
import ModalBugReport from '@/components/modal-bug-report/modal-bug-report.vue';
import OverlayPanelNotification
  from '@/components/overlay-panel-notification/overlay-panel-notification.vue';
import config from '@/config/config.ts';
import {
  ERouteName,
} from '@/router/route-name.ts';
import {
  useAppStore,
} from '@/store/pinia/app.ts';
import {
  useCustomUserStorageStore,
} from '@/store/pinia/custom-user-storage.ts';
import {
  getUserInitials,
} from '@/utils/user-name.ts';

const I18N_PREFIX = 'components/app/app-actions';

const appStore = useAppStore();
const route = useRoute();
const router = useRouter();
const customUserStorageStore = useCustomUserStorageStore();
appStore.loadCurrentUser();

const userMenu = shallowRef<InstanceType<typeof PrmMenu>>();
const showModalBugReport = shallowRef(false);

const initials = computed<string>(() => {
  if (appStore.user === undefined) {
    return '';
  }
  // belows syntax is able to prevent lone surrogates on unicode characters, such as
  // chinese characters, from being split into two characters
  return getUserInitials(appStore.user!.firstName, appStore.user!.lastName);
});

function logout() {
  keycloakAuthenticationHandler.logout();
}

const items = computed(() => {
  return [
    {
      separator: true,
    },
    {
      key: 'save-page-as-home',
      label: i18n.global.t(`${I18N_PREFIX}.savePageAsDefault`),
      iconComponent: HomeIcon,
      disabled: route.name === ERouteName.HOME,
      command: () => {
        customUserStorageStore.createOrUpdateCustomUserSettings({
          ...customUserStorageStore.customUserSettings!,
          homePageRoute: {
            name: router.currentRoute.value.name! as any,
            params: router.currentRoute.value.params || undefined,
            query: router.currentRoute.value.query || undefined,
          },
        });
      },
    },
    {
      key: 'reset-home',
      label: i18n.global.t(`${I18N_PREFIX}.resetDefaultPage`),
      icon: 'pi pi-undo',
      disabled: !customUserStorageStore.customUserSettings!.homePageRoute,
      command: () => {
        customUserStorageStore.createOrUpdateCustomUserSettings({
          ...customUserStorageStore.customUserSettings!,
          homePageRoute: undefined,
        });
      },
    },
    {
      separator: true,
    },
    {
      key: 'logout',
      label: i18n.global.t('common.logout'),
      icon: 'pi pi-sign-out',
      style: {
        color: 'var(--error-color)',
      },
      command: logout,
    },
    {
      separator: true,
    },
    {
      key: 'bug-report',
      label: i18n.global.t(`${I18N_PREFIX}.bugReport`),
      icon: 'pi pi-megaphone',
      command: () => {
        // Sentry may not be available on the environment (.e.g.: OnPrem installations).
        // In these cases we fall back to an email to our support email address.
        const isSentryInitialized = !!Sentry.getClient();
        if (isSentryInitialized) {
          showModalBugReport.value = true;
        } else {
          const tenantName = config.VITE_APP_TENANT_NAME ?? 'Unknown Tenant';
          const subject = encodeURIComponent(`[Bug Report] Qmulus v${packageInfo.version} - ${tenantName}`);
          const body = encodeURIComponent(
            `Tenant: ${tenantName}\n`
              + `App Version: ${packageInfo.version}\n`
              + `User: ${appStore.user?.displayName || 'Unknown'}\n`
              + `URL: ${window.location.href}\n\n`
              + `Please describe the issue:\n`,
          );
          window.location.href = `mailto:${config.VITE_APP_SUPPORT_EMAIL}?subject=${subject}&body=${body}`;
        }
      },
    },
    {
      separator: true,
    },
  ];
});
</script>

<style scoped lang="scss">
.navbar-header__menu-item {
  text-align: start;

  // TODO: investigate, maybe this: "overflow-wrap: anywhere;"
  /* stylelint-disable-next-line declaration-property-value-keyword-no-deprecated */
  word-break: break-word;

  svg {
    width: 16px;
    height: 16px;
  }
}
</style>
